import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { Link, useNavigate, useParams } from "react-router-dom";
import StatusBadge from "../../components/StatusBadge";
import { useContext, useEffect, useMemo, useState } from "react";
import axiosClient from "../../services/axiosClient";
import Loader, { LOADER_SIZES } from "../../components/Loader";

import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import internalLinkIcon from "../../assets/internal-link.png";

import ErrorBox from "../../components/ErrorBox";
import ErrorMessage from "../../components/ErrorMessage";
import InformationBox from "../../components/InformationBox";

import SectionDivider from "../../components/SectionDivider";
import editIcon from "../../assets/edit_icon.png";

import {
  buildInvoiceDocUrl,
  fetchFile,
  getFileType,
  sortByCustomOrder,
} from "../../utils/files";

import {
  Radio,
  RadioGroup,
  RadioGroupItem,
  RadioLabel,
} from "../../components/Radio";

import CustomButton from "../../components/CustomButton";
import analyzeIcon from "../../assets/analyzeIcon.png";
import OrderExecutionIcon from "../../assets/order_execution.png";
import CancelOrderExecutionIcon from "../../assets/cancel_order_execution.png";
import LoaderWithProgress from "../../components/LoaderWithProgress";
import {
  analyzeInvoiceForBank,
  getAnalyzedInvoice,
  createAnalyzedApplication,
} from "../../services/bank/banksAnalyzeService";

import TextDiv from "../../components/TextDiv";
import { getCurrency } from "../../utils/currency";
import DocumentsGrid from "../../components/DocumentsGrid";
import { UserAuth } from "../../utils/AuthContext";
import CustomInput from "../../components/CustomInput";
import { updateBankStatus } from "../../services/bank/bankServices";
import AreYouSureModal from "../../components/AreYouSureModal";
import CommonRejectionsReasons from "../../components/CommonRejectionsReasons";

import useLanguage from "../../utils/useLanguage";
import CollapsibleCard from "../attendance-system/CollapsibleCard";
import Timeline from "../../components/Timeline";
import ShakyInfoBox from "../../components/ShakyInfoBox";
import BackToDashboard from "../../components/BackToDashboard";
import { resolveIcon } from "../../utils/resolveIcon";

const BanksInvoicesApplicationPayments = () => {
  // ========================================
  // HOOKS
  // ========================================
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();

  // ========================================
  // STATE - Data
  // ========================================
  const [payments, setPayments] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [subCompany, setSubCompany] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const [invoiceStatus, setInvoiceStatus] = useState(
    invoiceData?.status || null,
  );
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentUserStatus, setCurrentUserStatus] = useState(null);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [sender, setSender] = useState(null);

  // ========================================
  // STATE - Loading & Errors
  // ========================================
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [isFetchingInvoiceDocs, setIsFetchingInvoiceDocs] = useState(true);
  const [fetchingInvoiceDocsError, setFetchingInvoiceDocsError] = useState("");
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [stateUpdateLoader, setStateUpdateLoader] = useState(false);

  // ========================================
  // STATE - UI & Flags
  // ========================================
  const [isFileAlreadyAnalyzed, setIsFileAlreadyAnalyzed] = useState(false);
  const [showPaymentReminder, setShowPaymentReminder] = useState(false);

  // creator related states
  const [isOrderCreatorRole, setIsOrderCreatorRole] = useState(false);
  const [orderHasCreator, setOrderHasCreator] = useState(false);
  const [isTheOrderCreator, setIsTheOrderCreator] = useState(false);
  const [creatorCanProceed, setCreatorCanProceed] = useState(true);

  // auditor related states
  const [isOrderAuditorRole, setIsOrderAuditorRole] = useState(false);
  const [orderHasAuditor, setOrderHasAuditor] = useState(false);
  const [isTheOrderAuditor, setIsTheOrderAuditor] = useState(false);
  const [auditorCanProceed, setAuditorCanProceed] = useState(false);

  const [leaveOrderExcution, setLeaveOrderExcution] = useState(false);
  const [isOrderExecutionDisabled, setIsOrderExecutionDisabled] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    completionState: "",
    subcompanySelection: "",
    supplierSelection: "",
    rejectionReason: "",
  });

  // ========================================
  // CONSTANTS
  // ========================================
  const tableTitles = [
    {
      value: t("amount"),
      keepOnMobile: true,
    },
    {
      value: t("transactionNum"),
      keepOnMobile: true,
    },
    {
      value: t("Date"),
      keepOnMobile: true,
    },
    {
      value: "creator",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
    {
      value: "auditor",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
    {
      value: "executer",
      keepOnMobile: true,
    },
    {
      value: "status",
      keepOnMobile: true,
    },
  ];

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const baseUrl = `${process.env.REACT_APP_URL}/multiContainerInvoiceWithoutC/getInvoiceFile`;

  // ========================================
  // MEMOIZED VALUES
  // ========================================
  const filesTitles = useMemo(
    () => ({
      invoice: t("invoice"),
      supplierCertificate: t("supplierCertificate"),
      articlesOfAssociation: t("articlesOfAssociation"),
      BaghdadConfirmation: t("confirmDocument"),
      BaghdadEntryRequest: t("transactionRequest"),
      BaghdadPaymentDetails: t("inforamtionRequest"),
      BaghdadPromise: t("declarationAndUndertaking"),
      BaghdadTransactionRequest: `${t("financialTransferRequestForm")}`,
    }),
    [t],
  );

  const sortedInvoiceDocs = useMemo(() => {
    const orderedKeys = [
      "invoice",
      "supplierCertificate",
      "articlesOfAssociation",
      "BaghdadTransactionRequest",
    ];
    return sortByCustomOrder(invoiceDocs, orderedKeys, "type");
  }, [invoiceDocs]);

  const canAccessLink = () => {
    const userRole = localStorage.getItem("role");
    const orgType = localStorage.getItem("organizationType");

    if (userRole) {
      return userRole === "applicationsCreator" && invoiceData.creator
        ? true
        : userRole === "applicationsAuditor" && invoiceData.auditor
          ? true
          : userRole === "paymentExecutor" &&
              invoiceData.auditor &&
              invoiceData.auditorStatus === "ACCEPTED"
            ? true
            : false;
    }

    if (orgType) {
      return orgType === "BANK";
    }
  };

  const tableRows = useMemo(
    () =>
      payments.map((payment) => [
        {
          value: `${payment?.amount?.toFixed(4)} ${getCurrency(payment)}`,
          type: canAccessLink() ? TABLE_CELL_TYPES.LINK : TABLE_CELL_TYPES.TEXT,
          to: true
            ? `/banks-invoices-applications/${id}/payments/${payment.id}`
            : undefined,
          keepOnMobile: true,
        },
        {
          value: (
            <p className="t-bold_bold royal-green">
              {payment?.transactionNumber || "-"}
            </p>
          ),
          type: TABLE_CELL_TYPES.ELEMENT,
          keepOnMobile: true,
        },
        {
          value: payment.date,
          type: TABLE_CELL_TYPES.DATE,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.TEXT,
          value: <div>
            <div>{payment?.creator?.username || t("none")}</div>
            {payment?.creationDate && <div className={"t-text-mini u-margin--top_100 t-bold_bold "} >{formatDateToDDMMYYYY(payment?.creationDate)}</div>}
            {console.log(payment)}
          </div>,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.ELEMENT,
          value:
            payment.creatorStatus === "NOT_STARTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment.creatorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"pending"} />
            ) : payment.creatorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment.creatorStatus === "ACCEPTED" ? (
              <StatusBadge color={"green"} title={"approved"} />
            ) : null,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.TEXT,
          value:<div>
          <div>{payment?.auditor?.username || t("none")}</div>
          {payment?.auditionDate && <div className={"t-text-mini u-margin--top_100 t-bold_bold "}>{formatDateToDDMMYYYY(payment?.auditionDate)}</div>}

        </div>,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.ELEMENT,
          value:
            payment.auditorStatus === "NOT_STARTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment.auditorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"pending"} />
            ) : payment.auditorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment.auditorStatus === "ACCEPTED" ? (
              <StatusBadge color={"green"} title={"approved"} />
            ) : null,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.TEXT,
          value:<div>
          <div>{payment?.executor?.username || t("none")}</div>
          {payment?.executionDate && <div className={"t-text-mini u-margin--top_100 t-bold_bold "}>{formatDateToDDMMYYYY(payment?.executionDate)}</div>}

        </div>,
          keepOnMobile: true,
        },
        {
          type: TABLE_CELL_TYPES.ELEMENT,
          value:
            payment.executorStatus === "NOT_EXECUTED" ? (
              <StatusBadge color={"gray"} title={"notStarted"} />
            ) : payment.executorStatus === "PENDING" ? (
              <StatusBadge color={"blue"} title={"inProgress"} />
            ) : payment.executorStatus === "REJECTED" ? (
              <StatusBadge color={"red"} title={"declined"} />
            ) : payment.executorStatus === "EXECUTED" ? (
              <StatusBadge color={"green"} title={"executed"} />
            ) : null,
          keepOnMobile: true,
        },
      ]),
    [payments, isOrderExecutionDisabled, id, t],
  );

  // ========================================
  // API HANDLERS
  // ========================================
  const getInvoicePayments = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosClient.get(`/bankAndSubcompany/getAll/${id}`);
      const supplier =
        response.data.invoicePayments.at(0)?.multiContainerInvoice.supplier;
      const subCompany =
        response.data.invoicePayments.at(0)?.multiContainerInvoice.subCompany;

      setSupplier(supplier);
      setSubCompany(subCompany);
      setPayments(response.data.invoicePayments);
      setInvoiceData(response.data.invoice);
    } catch (error) {
      console.error(error);
      setError("errorGettingInvoicePayments");
    } finally {
      setLoading(false);
    }
  };

  async function getInvoiceDocs() {
    try {
      setFetchingInvoiceDocsError("");
      setIsFetchingInvoiceDocs(true);

      const response = await axiosClient.get(
        `/multiContainerInvoiceWithoutC/invoiceMainFiles/${id}`,
      );
      setInvoiceDocs(response.data?.data?.documents);
      setIsFileAlreadyAnalyzed(response.data?.data?.analyzed);
    } catch (error) {
      console.error(error);
      setFetchingInvoiceDocsError("errorGettingInvoicePayments");
    } finally {
      setIsFetchingInvoiceDocs(false);
    }
  }

  const fetchAnalyzedInvoice = async () => {
    try {
      const res = await getAnalyzedInvoice(id);

      // res.data contains: rejectionReason, creatorStatus, auditorStatus, etc.
      const analyzed = res.data;

      // Merge into invoiceData so badges stay in sync
      setInvoiceData((prev) => ({
        ...(prev || {}),
        rejectionReason: analyzed.rejectionReason,
        creatorStatus: analyzed.creatorStatus ?? prev?.creatorStatus,
        auditorStatus: analyzed.auditorStatus ?? prev?.auditorStatus,
        bankStatus: analyzed.bankStatus ?? prev?.bankStatus,
        toHongKong: analyzed.toHongKong ?? prev?.toHongKong,
        rejectionReasonsHistories:
          analyzed.rejectionReasonsHistories ?? prev?.rejectionReasonsHistories,
        notes: analyzed.notes ?? prev?.notes,
        notesForBank: analyzed.notesForBank ?? prev?.notesForBank,
      }));

      const sender = analyzed.sender;
      setSender(sender);

      setRejectionReason(analyzed.rejectionReason);
    } catch (err) {
      // If there's no analyzed record yet, just ignore
      console.log(
        "No analyzed invoice yet or error fetching analyzed invoice",
        err,
      );
    }
  };

  const handleAnalyzeInvoice = async () => {
    setAnalyzeLoading(true);
    setAnalyzeError("");

    window.open(window.location.href, "_blank");
    try {
      // Convert PDFs to File objects
      const invoiceFile = invoiceDocs.find((doc) => doc.type === "invoice");

      const bankAppFile = invoiceDocs.find(
        (doc) => doc.type === "BaghdadTransactionRequest",
      );

      // Certficate and associations
      const certFile = invoiceDocs.find(
        (doc) => doc.type === "supplierCertificate",
      );
      const assocFile = invoiceDocs.find(
        (doc) => doc.type === "articleOfAssociation",
      );

      let invoiceBlob;
      if (invoiceFile) {
        invoiceBlob = await fetchFile(buildInvoiceDocUrl(invoiceFile), "pdf");
      }
      let bankAppBlob;
      if (bankAppFile) {
        bankAppBlob = await fetchFile(
          buildInvoiceDocUrl(bankAppFile),
          getFileType(bankAppFile.filePath),
        );
      }
      let certBlob;
      if (certFile) {
        certBlob = await fetchFile(buildInvoiceDocUrl(certFile), "pdf");
      }
      let assocBlob;
      if (assocFile) {
        assocBlob = await fetchFile(buildInvoiceDocUrl(assocFile), "pdf");
      }
      const formData = new FormData();
      formData.append("invoice", invoiceBlob);
      formData.append("bankForm", bankAppBlob);

      formData.append("supplierCertificate", certBlob);

      if (invoiceData.toHongKong) {
        formData.append("articlesOfAssociation", assocBlob);
      }

      const response = await analyzeInvoiceForBank(formData);

      if (response.status === 200) {
        navigate(`/bank-analyze-invoice?invoiceId=${id}`, {
          state: {
            data: {
              analyzedData: response.data || {},
              invoiceId: id,
              sender: subCompany,
              receiver: supplier,
              creator: {
                ...invoiceData.creator,
              },
              creatorStatus: invoiceData.creatorStatus,
              auditor: {
                ...invoiceData.auditor,
              },
              auditorStatus: invoiceData.auditorStatus,
              toHongKong: invoiceData.toHongKong,
            },
          },
        });
      }
    } catch (error) {
      console.log("error analyzing invoice", error);
      setAnalyzeError("errorAnalyzingInvoice");
    } finally {
      setAnalyzeLoading(false);
    }
  };

  const handleSave = async () => {
    // Only save when in edit mode
    if (!isEditing) return;

    const userId = userData?.userId || localStorage.getItem("userId");

    const isCreator =
      invoiceData?.creatorId === Number(userId) ||
      invoiceData?.creatorId === userId;
    const isAuditor =
      invoiceData?.auditorId === Number(userId) ||
      invoiceData?.auditorId === userId;

    // ---- Validation ----
    const newErrors = {};

    if (!currentUserStatus || currentUserStatus === "PENDING") {
      newErrors.completionState = "PleaseSelectCompletionState";
    }

    if (currentUserStatus === "REJECTED" && !rejectionReason.trim()) {
      newErrors.rejectionReason = "PleaseEnterRejectionReason";
    }

    setValidationErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));

    // These two are used to reset the error
    setValidationErrors({});
    setTimeout(() => {
      setValidationErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));
    }, 10);

    if (newErrors.completionState || newErrors.rejectionReason) {
      return;
    }

    // const status = currentUserStatus === "completed";
    const status = currentUserStatus;

    try {
      setIsLoadingSave(true);

      // 1) Reuse any existing analyzed data (if it exists)
      let applicationData = null;
      try {
        const analyzedRes = await getAnalyzedInvoice(id);
        applicationData = analyzedRes.data?.applicationData || null;
      } catch (err) {
        // No existing analyzed invoice -> fine, we still save status + rejectionReason
      }

      // 2) Call the same endpoint as BanksAnalyzeInvoice (POST /analyzedInvoice)
      await createAnalyzedApplication({
        applicationData,
        invoiceNumber: invoiceData?.invoiceNumber,
        date: invoiceData?.invoiceDate,
        creatorId: isCreator ? userId : null,
        auditorId: isAuditor ? userId : null,
        senderId: subCompany?.id,
        receiverId: supplier?.id,
        invoiceId: id,
        status,
        rejectionReason,
      });

      // 3) 🔁 Fetch the fresh invoice with updated creator/auditor + statuses
      const refreshed = await getAnalyzedInvoice(id);

      // This will contain creatorStatus / auditorStatus / rejectionReason from DB
      setInvoiceData(refreshed.data);
      setRejectionReason(refreshed.data?.rejectionReason || "");

      // keep helper states roughly in sync (useEffect on invoiceData will also run)
      setInvoiceStatus(status);
      setCurrentUserStatus(status ? "completed" : "incomplete");

      setIsEditing(false);
    } catch (error) {
      console.error(error);
      setError("errorSavingAnalyzedApplication");
    } finally {
      setIsLoadingSave(false);
    }
  };

  const toggleOrderExecution = async () => {
    if (!invoiceData || !userData) return;

    setIsOrderExecutionDisabled((prev) => !prev);

    const userId = userData?.userId || localStorage.getItem("userId");

    try {
      setIsLoadingSave(true);
      setStateUpdateLoader(true);

      await updateBankStatus({
        invoiceId: id,
        userId: userId,
        bankStatus: null,
      });

      const refreshed = await getAnalyzedInvoice(id);

      setInvoiceData(refreshed.data);
    } catch (error) {
      console.error(error);
      console.log(error);
      const errorMessage = error.response.data.error;
      setError(errorMessage);
    } finally {
      setIsLoadingSave(false);
      setStateUpdateLoader(false);
    }
  };

  // ========================================
  // EFFECTS
  // ========================================
  useEffect(() => {
    const load = async () => {
      await getInvoicePayments(); // sets invoiceData from /bankAndSubcompany/getAll/:id
      await getInvoiceDocs();

      // 🔥 Now pull rejectionReason + statuses from analyzedInvoice endpoint
      await fetchAnalyzedInvoice();
    };

    load();
  }, []);

  useEffect(() => {
    if (!invoiceData) return;

    const rawUserId = userData?.userId ?? localStorage.getItem("userId");
    if (!rawUserId) return;

    const userIdStr = String(rawUserId);
    const creatorIdStr = invoiceData?.creator
      ? String(invoiceData?.creator?.id)
      : null;
    const auditorIdStr = invoiceData?.auditor
      ? String(invoiceData.auditor?.id)
      : null;

    const isCreatorOnThisInvoice = creatorIdStr === userIdStr;
    const isAuditorOnThisInvoice = auditorIdStr === userIdStr;

    // let userStatus = invoiceData?.status === true ? "ACCEPTED" : "REJECTED";
    let userStatus =
      invoiceData?.status !== undefined ? invoiceData?.status : null;

    let checkStatus = null;

    if (isCreatorOnThisInvoice) {
      userStatus =
        invoiceData?.creatorStatus === "ACCEPTED" ? "ACCEPTED" : "REJECTED";
      checkStatus = invoiceData?.creatorStatus;
    } else if (isAuditorOnThisInvoice) {
      userStatus =
        invoiceData?.auditorStatus === "ACCEPTED" ? "ACCEPTED" : "REJECTED";
      checkStatus = invoiceData?.auditorStatus;
    }

    const loggedInRole = userData?.role;

    const bankStatus = invoiceData?.bankStatus;

    // Enable order execution if:
    // 1. Status is NOT_STARTED (not claimed yet), OR
    // 2. Current user is the one who claimed it (creator/auditor)
    const shouldDisable =
      bankStatus !== "NOT_STARTED" &&
      !isCreatorOnThisInvoice &&
      !isAuditorOnThisInvoice;

    setIsOrderExecutionDisabled(shouldDisable);

    // For creator: disable cancel if they took any action or there's app data

    const hasCreatorAction =
      invoiceData.creator !== null &&
      invoiceData?.creatorStatus !== "NOT_STARTED";

    const hasApplicationData =
      invoiceData?.applicationData !== null &&
      invoiceData?.applicationData !== undefined;

    // For auditor: check if creator exists, completed, and last payment is accepted

    const isAuditor = loggedInRole === "applicationsAuditor";
    const hasCreator = invoiceData.creator !== null;
    const hasAuditor = invoiceData.auditor !== null;
    const creatorCompleted = invoiceData?.creatorStatus === true;
    const lastPayment = payments[payments.length - 1];
    const lastPaymentAccepted = lastPayment?.creatorStatus === true;

    const canAuditorProceed =
      isAuditor && hasCreator && creatorCompleted && lastPaymentAccepted;

    // New Fields
    setIsOrderCreatorRole(() => loggedInRole === "applicationsCreator");
    setIsOrderAuditorRole(() => loggedInRole === "applicationsAuditor");

    setIsTheOrderCreator(() => creatorIdStr === userIdStr);
    setAuditorCanProceed(canAuditorProceed);

    setIsTheOrderAuditor(() => auditorIdStr === userIdStr);

    // Set cancel disabled state
    if (isAuditor) {
      // Auditor can cancel only if they're the one who claimed it
      // setOrderHasCreator(!isAuditorOnThisInvoice);
      setOrderHasAuditor(hasAuditor);
    } else {
      // Creator can't cancel if they took action or there's app data
      setOrderHasCreator(hasCreator);
      setCreatorCanProceed(hasCreatorAction || hasApplicationData);
    }

    setInvoiceStatus(invoiceData.status);
    setCurrentUserStatus(checkStatus);
  }, [invoiceData, userData, payments, isTheOrderCreator]);

  useEffect(() => {
    if (isEditing) {
      setRejectionReason(invoiceData?.rejectionReason || "");
    }
  }, [isEditing, invoiceData?.rejectionReason]);

  // For buttons that should follow your rules:
  const isButtonDisabled = () => {
    const userId = userData?.userId || localStorage.getItem("userId");
    const userIdStr = String(userId);

    const hasCreator = invoiceData?.creator !== null;
    const hasAuditor = invoiceData?.auditor !== null;

    const isCurrentUserCreator =
      invoiceData?.creator?.id?.toString() === userIdStr;
    const isCurrentUserAuditor =
      invoiceData?.auditor?.id?.toString() === userIdStr;

    const isCreatorRole =
      userData?.role === "applicationsCreator" || userData?.role === "creator";
    const isAuditorRole = userData?.role === "applicationsAuditor";

    // Rule 3: If no creator AND no auditor exist, disable
    if (!hasCreator && !hasAuditor) {
      return true;
    }

    // Rule 1: If there's a creator
    if (hasCreator) {
      // If current user is the creator, enable (return false)
      if (isCurrentUserCreator) {
        return false;
      }
      // If current user is a different creator, disable (return true)
      if (isCreatorRole && !isCurrentUserCreator) {
        return true;
      }
    }

    // Rule 2: If there's an auditor
    if (hasAuditor) {
      // If current user is the auditor, enable (return false)
      if (isCurrentUserAuditor) {
        return false;
      }
      // If current user is a different auditor, disable (return true)
      if (isAuditorRole && !isCurrentUserAuditor) {
        return true;
      }
    }

    // Default: if user doesn't match any of the above conditions, disable
    return true;
  };

  // Add this helper function for the execute button visibility
  const shouldShowExecuteButton = () => {
    const userId = userData?.userId || localStorage.getItem("userId");
    const userIdStr = String(userId);

    const hasCreator = invoiceData?.creator !== null;
    const hasAuditor = invoiceData?.auditor !== null;

    const isCurrentUserCreator =
      invoiceData?.creator?.id?.toString() === userIdStr;
    const isCurrentUserAuditor =
      invoiceData?.auditor?.id?.toString() === userIdStr;

    const isCreatorRole =
      userData?.role === "applicationsCreator" || userData?.role === "creator";
    const isAuditorRole = userData?.role === "applicationsAuditor";

    const creatorTookAction =
      invoiceData?.creatorStatus === "ACCEPTED" ||
      invoiceData?.creatorStatus === "REJECTED";

    const auditorTookAction =
      invoiceData?.auditorStatus === "ACCEPTED" ||
      invoiceData?.auditorStatus === "REJECTED";

    const isExcuterRole = userData?.role === "paymentExecutor";

    if (isExcuterRole) {
      return false;
    }

    // Condition 2: Hide if creator took action (but not for auditors)
    if (hasCreator && creatorTookAction && !isAuditorRole) {
      return false;
    }

    // Condition 5: Hide if auditor took action (but not for creators)
    if (hasAuditor && auditorTookAction && !isCreatorRole) {
      return false;
    }

    // Condition 3: If there's a creator, hide for other creators (but not for auditors)
    if (hasCreator && isCreatorRole && !isCurrentUserCreator) {
      return false;
    }

    // Condition 4: If there's an auditor, hide for other auditors (but not for creators)
    if (hasAuditor && isAuditorRole && !isCurrentUserAuditor) {
      return false;
    }

    // Condition 1: Show if any of the original conditions are met
    return (
      isCurrentUserCreator ||
      isAuditorRole ||
      !hasCreator ||
      hasAuditor ||
      isCurrentUserAuditor
    );
  };

  // ========================================
  // RENDER HELPERS
  // ========================================
  const ApplicationStatusSection = () => {
    return (
      <div
        className={`o-column o-column--gap-size_300 ${
          validationErrors.completionState ? "shake-animation-outline" : ""
        }`}
      >
        <RadioGroup>
          <RadioGroupItem>
            <Radio
              id="option1"
              value="ACCEPTED"
              checked={currentUserStatus === "ACCEPTED"}
              onChange={(e) => {
                setValidationErrors({
                  completionState: false,
                });
                setCurrentUserStatus(e.target.value);
              }}
            />
            <RadioLabel htmlFor="option1">{t("completed")}</RadioLabel>
          </RadioGroupItem>

          <RadioGroupItem>
            <Radio
              id="option2"
              value="REJECTED"
              checked={currentUserStatus === "REJECTED"}
              onChange={(e) => {
                setValidationErrors({
                  completionState: false,
                });
                setCurrentUserStatus(e.target.value);
              }}
            />
            <RadioLabel htmlFor="option2">{t("incomplete")}</RadioLabel>
          </RadioGroupItem>
        </RadioGroup>
      </div>
    );
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <PageWrapper withBackButton>


      <h3>{t("invoiceDetails")}</h3>

      <div className="o-row o-row--horizontal-align_space-between u-margin--top_400">
        <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
          <span>
            {userData?.userType === "organization"
              ? `${t("organization")}:`
              : `${t("user")}:`}
          </span>
          <span>{`${localStorage.getItem("username")}`}</span>
        </div>

        {shouldShowExecuteButton() && (
          <CustomButton
            icon={
              orderHasCreator && isTheOrderCreator
                ? CancelOrderExecutionIcon
                : orderHasAuditor || isTheOrderAuditor
                  ? CancelOrderExecutionIcon
                  : OrderExecutionIcon
            }
            title={
              orderHasCreator && isTheOrderCreator
                ? t("cancelOrderExecution")
                : orderHasAuditor || isTheOrderAuditor
                  ? t("cancelOrderExecution")
                  : t("orderExecution")
            }
            variant="blank"
            onClick={() => {
              orderHasCreator && isTheOrderCreator
                ? setLeaveOrderExcution(true)
                : orderHasAuditor || isTheOrderAuditor
                  ? setLeaveOrderExcution(true)
                  : toggleOrderExecution();
            }}
            disabled={
              (orderHasCreator && !isTheOrderCreator) ||
              (orderHasAuditor && !isTheOrderAuditor) ||
              stateUpdateLoader
            }
          />
        )}
      </div>

      <SectionDivider title={t("creatorAndAuditor")} />
      {loading && <Loader size={LOADER_SIZES.LARGE} />}
      {!loading &&
        !error &&
        (stateUpdateLoader ? (
          <Loader size={LOADER_SIZES.LARGE} />
        ) : (
          <div className="o-column o-column--gap-size_600 u-margin_0">
            {/* creator status */}

            {invoiceData.creator && (
              <div className="o-column o-column--gap-size_200">
                <TextDiv
                  title="creator"
                  value={invoiceData?.creator?.username}
                  divClasses="o-row "
                  pClasses="t-bold_semi-bold u-min-width-specific_200"
                />

                <div className="o-row o-row--vertical-align_center">
                  <label className="u-min-width-specific_200">
                    {t("completionStatus")}:
                  </label>
                  {invoiceData?.creatorStatus === "NOT_STARTED" ? (
                    <StatusBadge color={"gray"} title={"notStarted"} />
                  ) : invoiceData?.creatorStatus === "PENDING" ? (
                    <StatusBadge color={"blue"} title={"inProgress"} />
                  ) : invoiceData?.creatorStatus === "REJECTED" ? (
                    <StatusBadge color={"red"} title={"declined"} />
                  ) : invoiceData?.creatorStatus === "ACCEPTED" ? (
                    <StatusBadge color={"green"} title={"approved"} />
                  ) : null}
                </div>
              </div>
            )}

            {/* auditor status */}
            {invoiceData.auditor && (
              <div className="o-column o-column--gap-size_200">
                <TextDiv
                  title="auditor"
                  value={invoiceData?.auditor?.username}
                  divClasses="o-row"
                  pClasses="t-bold_semi-bold u-min-width-specific_200"
                />

                <div className="o-row o-row--vertical-align_center">
                  <label className="u-min-width-specific_200">
                    {t("completionStatus")}:
                  </label>
                  {invoiceData?.auditorStatus === "NOT_STARTED" ? (
                    <StatusBadge color={"gray"} title={"notStarted"} />
                  ) : invoiceData?.auditorStatus === "PENDING" ? (
                    <StatusBadge color={"blue"} title={"inProgress"} />
                  ) : invoiceData?.auditorStatus === "REJECTED" ? (
                    <StatusBadge color={"red"} title={"declined"} />
                  ) : invoiceData?.auditorStatus === "ACCEPTED" ? (
                    <StatusBadge color={"green"} title={"approved"} />
                  ) : null}
                </div>
              </div>
            )}
          </div>
        ))}
      <div className="u-margin--top_700" />
      {!isFetchingInvoiceDocs &&
        !fetchingInvoiceDocsError &&
        sortedInvoiceDocs?.length === 0 && (
          <InformationBox>{t("noDocuments")}</InformationBox>
        )}
      {isFetchingInvoiceDocs && <Loader size={LOADER_SIZES.MEDIUM} />}
      {fetchingInvoiceDocsError && (
        <ErrorBox>{t(fetchingInvoiceDocsError)}</ErrorBox>
      )}

      <div className="">
        <SectionDivider title={t("senderAndReceiver")} />
        <div className="o-column o-column--gap-size_300">
          <div className="o-row o-row--vertical-align_center">
            <span className="u-min-width-specific_200 t-bold_semi-bold u-padding_0">
              {t("sender")}:{" "}
            </span>
            <a
              href={`/bank-subcompanies/${sender?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="u-bald-link o-row o-row--vertical-align_center o-row--gap-size_200"
            >
              <span>
                <img
                  src={internalLinkIcon}
                  alt=""
                  className="u-height-specific_20"
                />
              </span>
              <span>
                {currentDirection === "rtl"
                  ? subCompany?.nameInAr
                  : subCompany?.name}
              </span>
            </a>
          </div>
          <div className="o-row o-row--vertical-align_center">
            <span className="u-min-width-specific_200 t-bold_semi-bold">
              {t("receiver")}:{" "}
            </span>
            <a
              href={`/bank-suppliers/${supplier?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="u-bald-link o-row o-row--vertical-align_center o-row--gap-size_200"
            >
              <span>
                <img
                  src={internalLinkIcon}
                  alt=""
                  className="u-height-specific_20"
                />
              </span>
              <span>{supplier?.name}</span>
            </a>
          </div>
        </div>
      </div>

      <SectionDivider title={t("invoiceDocuments")} />
      {!isFetchingInvoiceDocs &&
        !fetchingInvoiceDocsError &&
        sortedInvoiceDocs.length > 0 && (
          <DocumentsGrid
            documents={sortedInvoiceDocs}
            filesTitles={filesTitles}
            baseUrl={baseUrl}
            filterType="invoice" // optional
            showSubname={true} // to show (lastPayment)
          />
        )}

      <SectionDivider title={t("otherDocs")} />
      {!isFetchingInvoiceDocs &&
        !fetchingInvoiceDocsError &&
        sortedInvoiceDocs.length > 0 && (
          <DocumentsGrid
            documents={sortedInvoiceDocs}
            filesTitles={filesTitles}
            baseUrl={baseUrl}
            filterType="non-invoice"
            showSubname={true}
          />
        )}

      {analyzeError && <ErrorBox>{t(analyzeError)}</ErrorBox>}
      {analyzeLoading && <LoaderWithProgress />}
      <div className="o-row o-row--gap-size_200 o-row--horizontal-align_end u-padding--bottom_500 u-margin--top_500">
        {isFileAlreadyAnalyzed && (
          <CustomButton
            icon={analyzeIcon}
            title={t("viewAnalyzeResults")}
            variant="blank"
            elementType={Link}
            to={`/bank-analyze-invoice?invoiceId=${id}`}
            // disabled={isButtonDisabled()}
          />
        )}
        {!isFileAlreadyAnalyzed && (
          <CustomButton
            icon={analyzeIcon}
            title={t("analyzeFiles")}
            variant="blank"
            onClick={handleAnalyzeInvoice}
            disabled={isButtonDisabled()}
          />
        )}
      </div>

      {invoiceData?.notesForBank && (
        <div>
          <SectionDivider title={t("senderNotes")} />

          <p>{invoiceData?.notesForBank}</p>
        </div>
      )}

      <div className="u-margin--top_700"></div>

      {!loading && (
        <SectionDivider>
          <p className="t-bold_bold t-text-large">{t("payments")}</p>
        </SectionDivider>
      )}

      <div>
        {error && <ErrorBox>{t(error)}</ErrorBox>}
        {!loading && !error && payments.length === 0 && (
          <InformationBox>{t("noPaymentsForInvoice")}</InformationBox>
        )}
        {!loading && !error && payments.length > 0 && (
          <RoundedTable titles={tableTitles} rows={tableRows} />
        )}

        {/* ---- Invoice Status Editor ---- */}
        <section
          className={`u-margin--top_700 ${
            validationErrors.rejectionReason ? "shake-animation-outline" : ""
          }`}
        >
          <div className="u-margin--bottom_500">
            {invoiceData?.rejectionReasonsHistories?.length > 0 && (
              <CollapsibleCard
                title={t("applicationDate")}
                body={
                  <Timeline>
                    {invoiceData?.rejectionReasonsHistories
                      ?.sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((history) => (
                        <div key={history.id}>
                          <span className="t-bold_bold">
                            {t("date")}:{" "}
                            {new Date(history.date).toLocaleDateString("en-UK")}
                          </span>
                          <p style={{ whiteSpace: "pre-line" }}>
                            {history.rejectionReason}
                          </p>

                          <div className="u-padding--top_200">
                            <div>
                              <span className="t-bold_bold t-text-mini">
                                {t("creator")}:{" "}
                              </span>
                              <span className="t-text-mini">
                                {history.creatorName || "-"}
                              </span>
                            </div>
                            <div>
                              <span className="t-bold_bold t-text-mini">
                                {t("auditor")}:{" "}
                              </span>
                              <span className="t-text-mini">
                                {history.auditorName || "-"}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </Timeline>
                }
              ></CollapsibleCard>
            )}
          </div>

          {isEditing ? (
            <>
              <CommonRejectionsReasons
                selectedReasons={selectedReasons}
                setSelectedReasons={setSelectedReasons}
                onRejectionReasonChange={setRejectionReason}
                currentRejectionReason={invoiceData?.rejectionReason}
              />

              <CustomInput
                type="textarea"
                title={`${t("notes")}:`}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
              {validationErrors.rejectionReason && (
                <ErrorMessage>
                  {t(validationErrors.rejectionReason)}
                </ErrorMessage>
              )}
            </>
          ) : (
            <div>
              <label className="t-bold_bold">{t("notes")}:</label>
              <p style={{ whiteSpace: "pre-line" }}>{rejectionReason}</p>
            </div>
          )}

          {isEditing ? (
            <div className="o-column o-column--gap-size_400 u-margin--top_400">
              <label className="t-bold_bold">{t("status")}:</label>
              <ApplicationStatusSection />
            </div>
          ) : null}
        </section>

        {isLoadingSave && <Loader />}

        {payments.length === 1 && showPaymentReminder && !isEditing && (
          <ShakyInfoBox
            autoHideDuration={10 * 1000}
            onHide={() => setShowPaymentReminder(false)}
          >
            <p>
              {t("dontForgetToUpdatePayment")}{" "}
              <a
                href={`/banks-invoices-applications/${id}/payments/${payments[0]?.id}`}
              >
                {t("clickHere")}
              </a>
            </p>
          </ShakyInfoBox>
        )}

        <section className="u-margin--top_700 o-column o-column--vertical-align_end">
          <div className="u-width--fit-content">
            {validationErrors.completionState && (
              <div className="o-row o-row--horizontal-align_end u-margin--top_200">
                <ErrorMessage>{t("selectCompletionStatus")}</ErrorMessage>
              </div>
            )}

            {error && (
              <div className="o-row o-row--horizontal-align_end u-margin--top_200">
                <ErrorMessage>{t(error)}</ErrorMessage>
              </div>
            )}

            {validationErrors.rejectionReason && (
              <ErrorMessage>{t(validationErrors.rejectionReason)}</ErrorMessage>
            )}
            <div className="o-row o-row--gap-size_300 o-row--horizontal-align_end u-margin--top_300">
              {!isEditing && (
                <CustomButton
                  onClick={() => {
                    // Sync rejectionReason with current invoiceData before editing
                    setRejectionReason(invoiceData?.rejectionReason || "");
                    setIsEditing(true);
                  }}
                  title={t("edit")}
                  variant={"blank"}
                  icon={editIcon}
                  iconSize={16}
                  disabled={isButtonDisabled()}
                />
              )}

              {isEditing && (
                <CustomButton
                  onClick={() => {
                    setIsEditing(false);
                    setValidationErrors({
                      completionState: "",
                      subcompanySelection: "",
                      supplierSelection: "",
                      // rejectionReason: "",
                    });
                  }}
                  title={t("cancel")}
                  variant={"blank"}
                  icon={closeIcon}
                  iconSize={15}
                />
              )}

              {isEditing && (
                <CustomButton
                  onClick={() => {
                    handleSave();
                    setShowPaymentReminder(true);
                  }}
                  title={t("save")}
                  icon={saveIcon}
                  iconSize={16}
                  disabled={isLoadingSave}
                />
              )}
            </div>
          </div>

          {leaveOrderExcution && (
            <AreYouSureModal
              onClose={() => setLeaveOrderExcution(false)}
              onConfirm={() => {
                toggleOrderExecution();
                setLeaveOrderExcution(false);
              }}
              customMessage="sureYouWantToLeaveOrder"
              closeTitle="cancel"
              confirmTitle="confirm"
              confirm={true}
              confirmIconType="whiteCheck"
            />
          )}
        </section>
      </div>
    </PageWrapper>
  );
};

export default BanksInvoicesApplicationPayments;
